import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Calendar, Tag, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import ScrollToTop from '../components/ScrollToTop';
import SEO from '../components/SEO';
import { FadeIn } from '../components/FadeIn';
import { BlurText } from '../components/ui/animated-blur-text';
import { getAllPosts, type BlogPost } from '../utils/blog';

const categories = [
  { key: 'all', label: '전체' },
  { key: 'web', label: '웹 개발' },
  { key: 'mobile', label: '모바일' },
  { key: 'solution', label: '솔루션' },
  { key: 'ai', label: 'AI/ML' },
];

function PostCard({ post, index }: { post: BlogPost; index: number }) {
  return (
    <FadeIn delay={index * 100}>
      <Link to={`/blog/${post.slug}`}>
        <motion.article
          className="group relative bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden"
          whileHover={{ y: -4 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          {/* Gradient accent */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-zinc-700 to-zinc-600 group-hover:from-zinc-500 group-hover:to-zinc-400 transition-all" />

          <div className="p-8">
            {/* Category & Date */}
            <div className="flex items-center gap-3 mb-4 text-sm text-zinc-500">
              <span className="px-2.5 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs font-medium">
                {categories.find(c => c.key === post.category)?.label || post.category}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(post.date).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white mb-3 group-hover:text-zinc-200 transition-colors">
              {post.title}
            </h2>

            {/* Description */}
            <p className="text-sm text-zinc-400 leading-relaxed mb-6 line-clamp-2">
              {post.description}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.slice(0, 4).map(tag => (
                <span
                  key={tag}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs bg-zinc-900/50 text-zinc-500 rounded-full border border-zinc-800/50"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>

            {/* Read more */}
            <div className="flex items-center gap-2 text-sm font-medium text-zinc-500 group-hover:text-white transition-colors">
              자세히 보기
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </motion.article>
      </Link>
    </FadeIn>
  );
}

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const allPosts = getAllPosts();
  const filteredPosts = activeCategory === 'all'
    ? allPosts
    : allPosts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="블로그"
        description="NaldaDev의 프로젝트 사례와 기술 블로그. 웹 개발, 모바일 앱, AI 솔루션 등 다양한 개발 경험을 공유합니다."
        url="/blog"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <BlurText
              text="프로젝트 블로그"
              className="text-4xl md:text-5xl font-bold mb-4"
              duration={0.8}
            />
          </FadeIn>
          <FadeIn delay={200}>
            <p className="text-lg text-zinc-400">
              성공적으로 완료한 프로젝트들의 기술적 여정을 공유합니다
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-4 pb-8">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-2 justify-center">
          {categories.map(cat => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                activeCategory === cat.key
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:text-white hover:border-zinc-700'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      {/* Posts Grid */}
      <section className="px-4 pb-32">
        <div className="max-w-4xl mx-auto">
          {filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredPosts.map((post, index) => (
                <PostCard key={post.slug} post={post} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-zinc-500">
              해당 카테고리의 포스트가 없습니다.
            </div>
          )}
        </div>
      </section>

      <ScrollToTop />
    </div>
  );
}
